<template>
  <div class="modal is-active" :class="modalPartnerClass">
    <div class="modal-background"></div>
    <div class="modal-card">
      <section class="modal-card-body" :class="{footerless : !this.hasFooter}">
        <div v-if="title" class="modal-header title mb-0 is-4 has-text-centered">{{title}}</div>
        <div v-if="text" class="modal-text is-centered columns has-text-centered" v-html="text"></div>
        <div v-if="hasInput" class="modal-input">
          <input id="inputField" class="input" type="text" :placeholder="inputPlaceHolder" v-model="inputValue" @keyup.enter="success" @keyup.esc="cancel"/>
          <div v-if="isInlineImage" class="inline-image control is-flex has-text-primary">
            <label class="radio is-size-5 has-text-weight-semibold"><input type="radio" name="answer" v-model="imageAlignment" value="left">&nbsp;Left</label>
            <label class="radio is-size-5 has-text-weight-semibold"><input type="radio" name="answer" v-model="imageAlignment" value="center">&nbsp;Center</label>
            <label class="radio is-size-5 has-text-weight-semibold"><input type="radio" name="answer" v-model="imageAlignment" value="right">&nbsp;Right</label>
            <input class="input" type="text" placeholder="Image Caption" v-model="caption" @keyup.enter="success" @keyup.esc="cancel"/>
          </div>
        </div>
        <p v-if="questions && questions.length" class="has-text-danger required-fields" style="text-align: right;">(&#42; = {{$t('yscCheck.required')}})</p>
        <section v-if="isDisplayingQuestions" class="submit-questions">
          <div class="questions is-block" v-for="(question, questionIndex) in questions" :key="question.id">
            <section v-if="question.type === 'text'">
              <label v-if="question.label.length >= 40 && !$isProjectboard" class="label">{{question.label}}</label>
              <label v-if="showLabels" class="label">{{question.labels.en}}<span v-if="!question.optional" class="has-text-danger required-fields">&#42;</span></label>
              <generic-input
                  v-model="question.givenAnswer"
                  :idAndName="question.question"
                  :placeholder="question.labels.en">
              </generic-input>
            </section>
            <fieldset :id="question.labels.en">
              <div v-if="question.type === 'radio'" class="radio-question" >
                <h4 class="option-title">{{ question.labels.en }}</h4>
                <div class="radio-container">
                  <label class="radio-option is-flex" v-for="option in question.answers" :key="option.id" >
                    <input class="radio-button" type="radio" :name="question.labels.en" :value="option.id" v-model="question.givenAnswer">
                    {{ option.value }}
                  </label>
                </div>
              </div>
            </fieldset>
            <section v-if="question.type === 'list'" class="question-inputs">
              <label v-if="question.labels.en.length >= 40" class="label">{{question.labels.en}}<span v-if="!question.optional" class="has-text-danger required-fields">&#42;</span></label>
              <generic-dropdown v-if="excludeListQuestions(question)"
                  v-model="question.givenAnswer"
                  :idAndName="question.question"
                  :items="question.answers"
                  :placeholder="question.labels.en.length < 40 ? question.labels.en : ''"
                  :isUp="questionIndex > (questions.length - 1)/2 && isMobile">
              </generic-dropdown>
              <div v-for="i in 3" :key="`award${i}`">
                <div v-if="getAwardQuestionName(i-1) === question.question" class="select">
                  <select v-model="chosenAward[i-1]" @change="setSSDInfo(chosenAward[i-1], question)">
                    <option v-for="(award, index) in ssdAwards" :key="index" :value="award">{{award.name}}</option>
                  </select>
                </div>
              </div>
              <div v-for="i in 2" :key="`scholarship${i}`">
                <div v-if="getScholarshipQuestionName(i-1) === question.question" class="select">
                  <select v-model="chosenScholarship[i-1]" @change="setSSDInfo(chosenScholarship[i-1], question)">
                    <option v-for="(award, index) in ssdScholarships" :key="index" :value="award">{{award.name}}</option>
                  </select>
                </div>
              </div>
            </section>
          </div>
        </section>
        <section v-if="showDeclineQuestions" class="submit-questions">
          <div class="questions is-block">
            <fieldset>
              <div class="radio-question">
                <div class="radio-container">
                  <label class="radio-option" v-for="option in declineReasons" :key="option.id" >
                    <br><input class="radio-button" type="radio" :name="option.Text" :value="option.id" v-model="reason">
                    {{ option.Text }}
                  </label>
                </div>
              </div>
              <h4 class="option-title mt-2">Add custom message <span v-if="['DSD', 'BSEF'].includes(fairAbbreviation)">(optional)</span></h4>
              <textarea v-if="showTextBox" class="question-inputs-decline mt-1 reason-ta" v-model="customMessage" data-lpignore="true"></textarea>
              <input v-else class="question-inputs-decline mt-1" v-model="customMessage" data-lpignore="true"/>
            </fieldset>
          </div>
        </section>
        <div v-if="errorMessage" class="help has-text-danger is-size-6 has-text-centered">{{ errorMessage }}</div>
      </section>
      <footer v-if="hasFooter" class="modal-card-foot btn-center">
        <div v-if="swapButtons">
          <button v-if="cancelText" @click="cancel" class="button cancel-button" :class="getGTMCancel()">{{cancelText}}</button>
          <button v-if="successText" @click="success" class="button success-button is-primary" :class="getGTMSuccess()">{{successText}}</button>
        </div>
        <div v-else>
          <button v-if="successText" @click="success" class="button success-button is-primary" :class="getGTMSuccess()">{{successText}}</button>
          <button v-if="cancelText" @click="cancel" class="button cancel-button" :class="getGTMCancel()">{{cancelText}}</button>
        </div>
        <div v-if="footerText" class="footer-text">{{footerText}}</div>
      </footer>
      <button v-if="hasClose" @click="close" class="delete close-button" aria-label="close"></button>
    </div>
  </div>
</template>

<script>
import imageProxy from '@/libs/image-proxy'
import componentState from '@/mixins/component-state'
import GenericDropdown from '@/components/Controls/GenericComponents/dropdown'
import GenericInput from '@/components/Controls/GenericComponents/input'
import projectTemplates from '@/mixins/project-templates'
import breakpoints from '@/libs/breakpoints'

export default {
  mixins: [componentState, projectTemplates],
  components: {
    GenericDropdown,
    GenericInput
  },
  props: ['hasClose', 'hasFooter', 'footerText', 'hasInput', 'filledInput', 'title', 'text',
    'isInlineImage', 'successText', 'cancelText', 'justifyButtons', 'gtmSuccess', 'gtmCancel',
    'templateID', 'projectID', 'questions', 'isDeclined', 'swapButtons', 'declineReasonsProp',
    'showTextBox', 'fairAbbreviation', 'submissions'],

  data: function () {
    return {
      seenModal: true,
      inputValue: '',
      caption: '',
      imageAlignment: 'center',
      errorMessage: '',
      declineReasons: [],
      reason: '',
      customMessage: '',
      declineStatus: {},
      allowProjectSubmission: false,
      abbreviation: '',
      BIOProject: false,
      nominatedAwards: [],
      chosenAward: [],
      chosenScholarship: [],
      notApplicableQuestions: ['N/A — Not Applicable', 'Not Applicable — N/A'],
      selectDeclineReason: 'Please select reason to decline project'
    }
  },
  mounted: function () {
    if (this.filledInput) {
      this.inputValue = this.filledInput
      document.getElementById('inputField').setAttribute('readonly', true)
    }

    if (this.showDeclineQuestions) {
      if (this.$isOhioScience) {
        this.declineReasons = this.declineReasonsProp
      } else {
        this.$store.dispatch('getDeclineReasons').then((response) => {
          this.declineReasons = response.reasons
        })
      }
    }
    if (this.$isOhioScience && this.projectID) {
      this.$store.dispatch('getFairsForProject', this.projectID)
    }
  },
  created: function () {
    this._callback('lockScrolling', false)
  },
  destroyed: function () {
    this._callback('unlockScrolling', true)
  },
  computed: {
    inputPlaceHolder: function () {
      return this.isInlineImage ? 'Image Url' : ''
    },
    modalPartnerClass: function () {
      return 'modal-' + process.env.APP_NAME.toLowerCase()
    },
    isDisplayingQuestions: function () {
      return this.templateID > 0 && this.projectID > 0
    },
    showDeclineQuestions: function () {
      return this.isDeclined && (this.$isIsef || this.$isOhioScience)
    },
    getFairByAbbreviation: function () {
      return this.$store.getters.fairs[this.abbreviation]
    },
    bioDeclinedProject: function () {
      return this.submissions.length !== 0 && this.templateID === this.BIOTemplate && this.submissions[0].status === 'STATUS_DECLINED'
    },
    isMobile: function () {
      return breakpoints.isMobile()
    },
    eligibleFairs: function () {
      return this.$store.getters.eligibleFairs
    },
    isSSDFair: function () {
      return this.eligibleFairs?.[0]?.abbreviation === 'SSD'
    },
    showLabels: function () {
      return this.$isProjectboard || (this.$isOhioScience && this.isSSDFair)
    },
    ssdAwards: function () {
      return this.$store.getters.ssdAwards
    },
    ssdScholarships: function () {
      return this.$store.getters.ssdScholarships
    }
  },
  methods: {
    success: function () {
      this.errorMessage = ''
      if (this.showDeclineQuestions) {
        var array = this.declineReasons
        var id = this.reason
        var text = ''
        array.forEach(function (entry) {
          var x = entry.id
          if (x === id) {
            text = entry.Text
          }
        })
        var params = [this.reason, this.customMessage, text]
        if (this.declineStatus) {
          if (!this.reason && (this.fairAbbreviation === 'DSD' || this.fairAbbreviation === 'BSEF' || this.fairAbbreviation === 'SSD')) {
            this.errorMessage = this.selectDeclineReason
          } else if (['BIO'].includes(this.fairAbbreviation) && this.customMessage === '') {
            this.errorMessage = 'Please enter reason to decline the project in above box.'
          } else {
            if (this.$isIsef && params[1] === '' && params[2] === '') {
              this.errorMessage = this.selectDeclineReason
              return
            } else {
              this.$emit('selectedReason', { status: this.declineStatus, reason: params })
            }
          }
        } else {
          this.$emit('selectedReason', params)
        }
      }
      if (this.isInlineImage) {
        const args = { url: this.inputValue, alignment: this.imageAlignment, caption: this.caption }
        this.$emit('modalSuccess', args)
      } if (this.isDisplayingQuestions && this.questions) {
        let tags = []
        const fairs = []
        let projectNominations = []
        const checkDuplicateNominations = []
        this.questions.forEach((question) => {
          if (!question.givenAnswer && !question.optional) {
            this.errorMessage = this.$t('projectPage.submissionModalError')
            return
          }
          if (question.givenAnswer) {
            let answer
            if (question.givenAnswer.tag) {
              answer = question.givenAnswer.tag
            } else if (question.givenAnswer.value) {
              answer = question.givenAnswer.value.replace('/', '-')
              answer = answer.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
              answer = answer.replace(/([ ()/])*/g, '')
            } else {
              answer = question.givenAnswer
            }
            if (this.$isOhioScience) {
              if (!this.isSSDFair && this.ProjectTemplates.includes(this.templateID) && !['SD-customer-bsef-no', 'SD-customer-scienceday-no', 'BelieveInOhio2023-customer-written-no', 'BelieveInOhio2023-customer-pitch-no'].includes(answer)) {
                tags.push(answer)
                if (['BSEF', 'SDDistrict', 'BIOWritten', 'BIOPitch'].includes(answer)) {
                  if (['SDDistrict'].includes(answer)) {
                    this.abbreviation = 'DSD'
                  } else if (['BIOWritten', 'BIOPitch'].includes(answer)) {
                    this.BIOProject = true
                  } else if (['BSEF'].includes(answer)) {
                    this.abbreviation = 'BSEF'
                  }
                  if (!this.BIOProject) {
                    fairs.push(this.getFairByAbbreviation.id)
                  }
                }
              } else if (this.isSSDFair) {
                if (!this.notApplicableQuestions.includes(question.givenAnswer)) {
                  checkDuplicateNominations.forEach((nomination) => {
                    if (nomination === question.givenAnswer) {
                      this.errorMessage = this.$t('projectPage.selectUniqueAward')
                    }
                  })
                }
                checkDuplicateNominations.push(question.givenAnswer)
                if (!this.notApplicableQuestions.includes(question.givenAnswer) && this.errorMessage === '') {
                  const nominationTag = question.givenAnswer.split(' ')[0]
                  tags.push(nominationTag)
                  projectNominations.push(this.nominatedAwards[question.question])
                }
              }
            } else {
              tags.push(answer)
            }
          }
        })
        if (this.errorMessage !== '' && this.isSSDFair) {
          tags = []
          projectNominations = []
          return
        }
        if (this.BIOProject || this.bioDeclinedProject) {
          this.abbreviation = 'BIO'
          fairs.push(this.getFairByAbbreviation.id)
        }
        if (this.isSSDFair) {
          this.abbreviation = 'SSD'
          fairs.push(this.getFairByAbbreviation.id)
        }
        if (fairs.length === 0 && this.$isOhioScience && !this.bioDeclinedProject && !this.isSSDFair) {
          this.errorMessage = this.$t('projectPage.selectFair')
          return
        }
        if (tags.length === 0 && !this.$isOhioScience && !this.$isProjectboard) {
          return this.$emit('modalSuccess')
        }
        const requiredQuestions = this.questions.filter((question) => !question.optional)
        if ((tags.length >= requiredQuestions.length && !this.$isOhioScience) || (this.$isOhioScience && tags.length > 0 && this.errorMessage === '')) {
          this.$store.dispatch('submitProjectTags', { projectID: this.projectID, tags }).then((response) => {
            // we need the tags to be saved on the project before we start submitting the projects to fairs
            if (this.$isOhioScience && fairs.length > 0) {
              this.$emit('modalSuccess', this.projectID, fairs)
            } else {
              this.$emit('modalSuccess')
            }
            if (this.isSSDFair && projectNominations.length > 0) {
              this.createNominations(projectNominations)
            }
          }, () => {
            this.errorMessage = this.$t('projectPage.failedToSaveSubmitAnswers')
          })
        } else {
          // this case handles a scenario where tags do not need to be created for the project but we need to submit it
          // to the fair anyway.
          if (this.$isOhioScience && fairs.length > 0 && this.errorMessage === '') {
            this.$emit('modalSuccess', this.projectID, fairs)
          }
        }
      } else {
        this.$emit('modalSuccess', this.hasInput ? { url: this.inputValue } : null)
      }
    },
    excludeListQuestions: function (question) {
      return !['SD2023Round2-Select-1stAward-choice', 'SD2023Round2-Select-2ndAward-choice', 'SD2023Round2-Select-3rdAward-choice', 'SD2023Round2-Select-1stScholarship-choice', 'SD2023Round2-Select-2ndScholarship-choice'].includes(question.question)
    },
    setSSDInfo: function (award, question) {
      if (!this.notApplicableQuestions.includes(question.question) && this.nominatedAwards[question.question] === undefined) {
        this.nominatedAwards[question.question] = award.id
      } else if (this.nominatedAwards[question.question] !== undefined) {
        this.nominatedAwards.splice(question.question)
        this.nominatedAwards[question.question] = award.id
      }
      question.givenAnswer = award.name
    },
    createNominations: function (nominations) {
      const data = {
        awards: nominations
      }
      this.$store.dispatch('createProjectNominations', { projectID: this.projectID, data })
    },
    cancel: function () {
      this.$emit('modalCancel')
    },
    close: function () {
      this.$emit('modalClose')
    },
    getGTMSuccess: function () {
      if (this.gtmSuccess) {
        return this.gtmSuccess
      }
    },
    getAwardQuestionName: function (index) {
      if (index === 0) {
        return 'SD2023Round2-Select-1stAward-choice'
      } else if (index === 1) {
        return 'SD2023Round2-Select-2ndAward-choice'
      } else if (index === 2) {
        return 'SD2023Round2-Select-3rdAward-choice'
      }
    },
    getScholarshipQuestionName: function (index) {
      if (index === 0) {
        return 'SD2023Round2-Select-1stScholarship-choice'
      } else if (index === 1) {
        return 'SD2023Round2-Select-2ndScholarship-choice'
      }
    },
    getGTMCancel: function () {
      if (this.gtmSuccess) {
        return this.gtmSuccess
      }
    },
    thumbnail: imageProxy.thumbnail
  }
}
</script>

<style lang="scss" scoped>
  @import '~styles/utils.scss';
  .label {
    font-size: 0.9rem;
  }
  .question-inputs {
    margin-bottom: 5rem;
    width: 90%rem;
    margin: auto;
  }

  .question-inputs-decline {
    margin-bottom: 1rem;
    width: 20em;
  }

  .close-button {
    position: absolute;
    right: 1.5rem;
    top: 1.5rem;
  }

  .modal-card {
    overflow: visible;
  }

  .modal-card-body {
    overflow: visible;
    padding: 15px;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
  }
  .oas-submit .modal-text {
    font-size: 0.9rem;
  }

  .modal-text {
    padding: 1rem;
  }

  .modal-header{
    padding: 1.5rem 3rem 1.5rem 3rem;
    margin: 0;
  }

  .footerless {
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
  }

  .modal-card-foot {
    padding: 1rem 3rem;
  }

  .btn-center {
    justify-content: center;
  }

  .button {
    border-radius: 0;
  }
  .modal-input {
    padding: 3rem;
  }

  .inline-image {
    margin-top: 1rem;
    margin-bottom: 1rem;
    width: 100%;
    justify-content: space-between;
  }

  .modal-make {
    @import './stylesMake.scss';
  }

  .modal-neweng {
    @import './stylesNeweng.scss';
  }

  .reason-ta {
    resize: none;
    height: 5rem;
    width: 100%;
    font-size: 1rem;
    padding: 10px;
  }
  .oas-submit .modal-card-body{
    overflow-y: auto;
  }
</style>
